import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Users, MapPin, UtensilsCrossed, Sun, X } from "lucide-react";
import { BRAND } from "@/lib/data";
import { useContent } from "@/lib/content";
import { Overline } from "@/components/Overline";
import { Seo } from "@/components/Seo";

const ease = [0.16, 1, 0.3, 1];
const facts = [
  { icon: Users, label: "85 Plätze drinnen" },
  { icon: Sun, label: "110 Plätze auf der Terrasse" },
  { icon: UtensilsCrossed, label: "Gruppen- & kinderfreundlich" },
  { icon: MapPin, label: "Im Herzen von Löbtau" },
];

export default function About() {
  const c = useContent();
  const t = c.text.about;
  const img = c.images;
  const gallery = (c.gallery && c.gallery.length ? c.gallery : [img.gallery1, img.gallery2, img.gallery3, img.gallery4]).filter(Boolean);
  const [lightbox, setLightbox] = useState(null);

  return (
    <div data-testid="about-page" className="bg-cream">
      <Seo page="about" />
      <section className="relative pt-44 pb-24 grain overflow-hidden">
        <div className="absolute inset-0">
          <img src={img.aboutPottery} alt="Über uns" className="w-full h-full object-cover animate-kenburns-slow" />
          <div className="absolute inset-0 bg-ink/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Overline light className="!flex justify-center">{BRAND.since} · {BRAND.family}</Overline>
          <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl text-cream tracking-tight">Über uns</h1>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease }}>
            <Overline>Der Geschmack Italiens</Overline>
            <h2 className="font-serif font-light text-4xl sm:text-5xl text-ink leading-tight mb-6">{t.introTitle}</h2>
            <p className="text-ink-soft font-light leading-relaxed mb-5">{t.introP1}</p>
            <p className="text-ink-soft font-light leading-relaxed">{t.introP2}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease }} className="relative overflow-hidden">
            <img src={img.terrace} alt="Terrasse" className="w-full h-[540px] object-cover animate-kenburns-slow" />
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-sand grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {facts.map((f, i) => (
            <motion.div key={f.label} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08, ease }} className="text-center group" data-testid={`fact-${i}`}>
              <f.icon className="mx-auto text-wine mb-4 transition-transform duration-500 group-hover:scale-110" size={30} strokeWidth={1.3} />
              <p className="font-serif text-xl text-ink">{f.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-12 space-y-12">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease }}>
            <Overline>Unser Team</Overline>
            <h3 className="font-serif text-3xl text-ink mb-4">{t.teamTitle}</h3>
            <p className="text-ink-soft font-light leading-relaxed">{t.teamText}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease }}>
            <Overline>Wo wir sind</Overline>
            <h3 className="font-serif text-3xl text-ink mb-4">{t.locationTitle}</h3>
            <p className="text-ink-soft font-light leading-relaxed">{t.locationText}</p>
          </motion.div>
        </div>
      </section>

      {c.team && c.team.length > 0 && (
        <section data-testid="team-section" className="py-24 md:py-32">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <Overline className="!flex justify-center">Die Familie</Overline>
              <h2 className="font-serif font-light text-4xl sm:text-5xl text-ink">Unser Team</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {c.team.map((m, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6, delay: i * 0.08, ease }} className="text-center group" data-testid={`team-member-${i}`}>
                  <div className="overflow-hidden mb-4 aspect-[3/4]">
                    <img src={m.image} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <h3 className="font-serif text-2xl text-ink">{m.name}</h3>
                  <p className="text-xs tracking-[0.15em] uppercase text-gold-dark mt-1">{m.role}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-24 md:py-32 bg-sand grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <Overline className="!flex justify-center">Galerie</Overline>
            <h2 className="font-serif font-light text-4xl sm:text-5xl text-ink">Momente bei La Contadina</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {gallery.map((g, i) => (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6, delay: i * 0.1, ease }} className={`overflow-hidden group cursor-pointer ${i === 0 ? "col-span-2 row-span-2" : ""}`} data-testid={`gallery-img-${i}`} onClick={() => setLightbox(g)}>
                <img src={g} alt={`Galerie ${i + 1}`} className={`w-full object-cover group-hover:scale-105 transition-transform duration-700 ${i === 0 ? "h-full min-h-[300px]" : "h-48 md:h-56"}`} />
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-16">
            <Link to="/kontakt#reserve" data-testid="about-reserve-btn" className="btn-pop bg-wine text-cream hover:bg-wine-dark px-10 py-4 uppercase text-xs tracking-[0.25em] inline-block">Besuchen Sie uns</Link>
          </div>
        </div>
      </section>

      {lightbox && (
        <div data-testid="lightbox" onClick={() => setLightbox(null)} className="fixed inset-0 z-[80] bg-ink/92 backdrop-blur-sm flex items-center justify-center p-6 cursor-zoom-out">
          <button data-testid="lightbox-close" className="absolute top-6 right-6 text-cream/80 hover:text-gold transition-colors" aria-label="Schließen"><X size={32} /></button>
          <motion.img initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, ease }} src={lightbox} alt="Galerie" className="max-h-[88vh] max-w-[92vw] object-contain shadow-2xl" />
        </div>
      )}
    </div>
  );
}
